/**
 * Reviewer-dashboard row shapes (Phase 3), camelCased mirrors of the tables described in
 * ../../../db/supabase/migrations/20260711000000_initial_schema.sql. Hand-rolled (not the
 * generated Database type) to match the existing web/ convention of small, explicit
 * interfaces per screen (see ../api.ts).
 */

export interface ValuerRow {
  id: string;
  displayName: string;
  email: string;
}

export interface BankRow {
  id: string;
  name: string;
}

export type ReportStatus =
  | 'uploaded'
  | 'queued'
  | 'checking'
  | 'checked'
  | 'reviewing'
  | 'done'
  | 'error'
  | 'changes_requested';

/**
 * Executive-summary extract written by the compliance worker
 * (worker/src/compliance/schemas.ts -> ReportSummarySchema), shown above the
 * compliance sections so the reviewer instantly knows what they are QA-ing.
 * Every field is quoted from the report; null when the report doesn't state it.
 */
export interface ReportSummary {
  reportType: string | null;
  propertyDescription: string | null;
  dwellingCount: number | null;
  dwellingType: string | null;
  landArea: string | null;
  buildingArea: string | null;
  averageDwellingSize: string | null;
  valueRateRange: string | null;
  priceRange: string | null;
  zoning: string | null;
  keyFacts: string[];
}

export interface ReportRow {
  id: string;
  addressRaw: string | null;
  addressNormalised: string | null;
  valuerId: string | null;
  ordererBankId: string | null;
  status: ReportStatus;
  submittedAt: string | null;
  firstSeenAt: string;
  checkedAt: string | null;
  reviewerNotes: string | null;
  /** Optional free-text note the valuer left at upload time, for the reviewer. Null = no note. */
  valuerNotes: string | null;
  createdAt: string;
  /** Optional valuer-supplied deadline ("needed by"). Null if not set. */
  neededByAt: string | null;
  /** Phase 7: round number for this valuer/address thread. 1 = the original upload. */
  revisionNumber: number;
  /** Phase 7: the prior revision this report supersedes (a resubmission). Null for the first submission. */
  revisionOfReportId: string | null;
  /** Executive-summary extract for the workspace panel; null until checked (or when extraction failed). */
  reportSummary: ReportSummary | null;
}

export interface DocumentRow {
  id: string;
  reportId: string;
  kind: 'report' | 'letter_of_instruction' | 'other';
  pageCount: number | null;
}

export interface CheckSchemaRow {
  checkKey: string;
  category: string;
  label: string;
  description: string | null;
  displayOrder: number;
}

export type CheckStatus = 'pass' | 'fail' | 'flag' | 'na';

export interface CheckResultRow {
  id: string;
  reportId: string;
  checkKey: string;
  status: CheckStatus;
  reason: string | null;
  pageRef: number | null;
  evidenceSnippet: string | null;
  confidence: number | null;
}

export interface RiskSchemaRow {
  riskKey: string;
  riskGroup: 'property' | 'market';
  label: string;
  description: string | null;
  displayOrder: number;
  commentRequiredAbove: number;
}

export type RiskStatus = 'ok' | 'missing_score' | 'missing_comment' | 'comment_inadequate';

export interface RiskResultRow {
  id: string;
  reportId: string;
  riskKey: string;
  score: number | null;
  commentPresent: boolean;
  commentText: string | null;
  pageRef: number | null;
  status: RiskStatus;
  reason: string | null;
  confidence: number | null;
}

export type ErrorType = 'spelling' | 'math' | 'locality' | 'other';
export type Severity = 'low' | 'medium' | 'high';

export interface ErrorFindingRow {
  id: string;
  reportId: string;
  errorType: ErrorType;
  description: string | null;
  pageRef: number | null;
  severity: Severity | null;
  evidenceSnippet: string | null;
  suggestedFix: string | null;
  confidence: number | null;
}

/** Phase 7: reviewer markup on the report PDF. Coordinates are normalised (0-1). */
export type AnnotationType = 'highlight' | 'note' | 'shape';

export interface RectCoordinates {
  x: number;
  y: number;
  w: number;
  h: number;
}
export interface PointCoordinates {
  x: number;
  y: number;
}
export type ShapeCoordinates =
  | ({ kind: 'rectangle' } & RectCoordinates)
  | { kind: 'freehand'; points: PointCoordinates[] };

export interface AnnotationRow {
  id: string;
  reportId: string;
  page: number;
  type: AnnotationType;
  coordinates: RectCoordinates | PointCoordinates | ShapeCoordinates;
  color: string;
  text: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Phase 7: one row per "send feedback to valuer" click. */
export interface FeedbackDispatchRow {
  id: string;
  reportId: string;
  dispatchedAt: string;
  dispatchedBy: string;
  annotatedCopyStoragePath: string;
  resendMessageId: string | null;
  valuerViewedAt: string | null;
}

/** Which document a finding's page_ref points into (see CONVENTIONS.md: no doc-kind column on results). */
export type TargetDoc = 'report' | 'loi';

/** Instruction-match checks are about the LOI; everything else is judged on the report itself. */
export function checkTargetDoc(category: string): TargetDoc {
  return category === 'Instruction match' ? 'loi' : 'report';
}
